import { ToastrService } from 'ngx-toastr';
import { SubjectDialogComponent } from './subject-dialog/subject-dialog.component';
import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { map, Observable, of, tap } from 'rxjs';
import { ConfigSection } from '../config-section';
import { ContactUsSubjectEditorFacadeService } from '@admin/containers/mojo-system-settings/sections/contact-us-subject/services/contact-us-subject-editor-facade.service';
import { ContactUsSubject, NotifyType } from './contact_us_subject-types';
import { ContactUsSubjectFormsService } from './services/contact-us-subject-forms.service';

@Component({
  selector: 'contact-us-subject',
  templateUrl: './contact-us-subject.component.html'
})
export class ContactUsSubjectsComponent extends ConfigSection<ContactUsSubject[]> {

  /**
   * An array of forms maintained with the edited items
   */
  forms: UntypedFormGroup[] = [];

  /**
   * A reference for the subject dialog which can be used for adding new items
   */
  @ViewChild(SubjectDialogComponent) subjectDialog;


  /**
   * The notification types
   */
  notifyTypes: NotifyType[] = [];

  /**
   * The array of contact us subjects, retrieved from the server and then edited in memory.
   * All written at once during save.
   */
  contactUsSubjects: ContactUsSubject[];

  /**
   * An array of subjects used for inline edit/cancel
   */
  private clonedSubjects: { [s: string]: ContactUsSubject; } = {};

  private isModified: boolean = false;

  constructor(
    private readonly facade: ContactUsSubjectEditorFacadeService,
    private readonly formService: ContactUsSubjectFormsService,
    private readonly toastrService: ToastrService) {
    super();
  }

  get modified(): boolean {
    return this.isModified;
  }

  get valid(): boolean {
    return (this.forms.length === 0 || this.forms.every(f => f.valid));
  }

  initialiseConfig(): Observable<ContactUsSubject[]> {
    this.facade.loadSubjects().subscribe(subjects => this.contactUsSubjects = subjects);
    this.facade.loadNotifyTypes().subscribe(notifyTypes => this.notifyTypes = notifyTypes);
    return of(this.contactUsSubjects);
  }

  /** this is required for us to be a configSection but is not really used by this component */
  createForm(model: ContactUsSubject[]): void {
    const defaultSubject = {
      Uri: undefined,
      Subject: '',
      NotifyType: this.notifyTypes && this.notifyTypes.length > 0 ? this.notifyTypes[0].Value : '',
      Emails: []
    };
    this.form = this.formService.setupContactUsSubjectForm(defaultSubject);
  }
  getNotificationLabel(notificationType: string) {
    const item = this.notifyTypes.find(x => x.Value === notificationType);
    return item ? item.Label : null;
  }

  submit(): Observable<boolean> {
    return this.facade.saveSubjects(this.contactUsSubjects).pipe(tap(t => {
      if (t.Message) {
        this.toastrService.info(t.Message);
      }
    }), map(t => true));
  }

  /** gets form for a row edit */
  getForm(subject: ContactUsSubject, index: number) {
    if (!this.forms[index]) {
      this.forms[index] = this.formService.setupContactUsSubjectForm(subject);
    }
    return this.forms[index];
  }

  /** Show the add new subject dialog */
  add() {
    this.subjectDialog.showAddNewSubjectDialog();
  }

  /**
  * clone the item so any edits can be cancelled
  * @param subject
  */
  onRowEditInit(subject: ContactUsSubject, index: number) {
    this.forms[index] = this.formService.setupContactUsSubjectForm(subject);
    this.clonedSubjects[index] = { ...subject };
  }

  /**
   * remove cloned item
   * @param subject
   */
  onRowEditSave(subject: ContactUsSubject, index: number) {
    this.isModified = true;
    this.formService.mapFormToSubject(this.forms[index], subject);
    delete this.clonedSubjects[index];
  }

  /**
   * restore the cloned item over the edited one
   * @param subject
   * @param index
   */
  onRowEditCancel(subject: ContactUsSubject, index: number) {
    this.contactUsSubjects[index] = this.clonedSubjects[index];
    delete this.clonedSubjects[index];
  }

  /**
   * Deletes a whole row
   */
  onRowDelete(subject, index) {
    this.isModified = true;
    this.contactUsSubjects.splice(index, 1);
  }

  /**
   * Handles the save event raised from the new item dialog
   * @param subject
   */
  handleNewSubjectSave(subject: ContactUsSubject) {
    this.isModified = true;
    this.contactUsSubjects.push(subject);
  }

}
